import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { ClaimableRewards, ClaimableSoonEpochRewards, MetricsEvents } from "@cede/types";
import { SafeMath, getRewardChain, getRewardsSafeModuleAddress } from "@cede/utils";
import { useDebugOverrideAddress } from "./useDebugOverrideAddress";
import { useHubUser } from "./useHubUser";
import { isConnectorConnectionError, WalletConnectionError } from "@/hooks/core/useAuthenticatedWalletConnection";
import { useSendTransactionWithConnection } from "@/hooks/core/useSendTransactionWithConnection";
import { isWalletActionError, useWalletActionGuard, WALLET_ACTION_ERROR_CODE } from "@/hooks/core/useWalletActionGuard";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { invalidateUserStats } from "@/hooks/data/userStatsCache";
import { usePrices } from "@/hooks/prices";
import { clientConfig } from "@/lib/config/client-config";
import {
  filterClaimedBatches,
  groupBatchesByChain,
  toClaimEpochKey,
  type ClaimItem,
} from "@/utils/rewards/groupClaimsByChain";
import * as Sentry from "@sentry/nextjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAddress, UserRejectedRequestError } from "viem";
import { arbitrum, base, mainnet, sepolia } from "viem/chains";
import { useSwitchChain, usePublicClient } from "wagmi";

/**
 * Chain IDs rewards can be distributed on. Mirrors `SUPPORTED_REWARD_CHAINS` in
 * `@cede/utils` — kept as a literal map here so the per-chain `usePublicClient`
 * calls below have a fixed, statically-known set (Rules of Hooks).
 */
const REWARD_CHAIN_IDS = {
  arbitrum: arbitrum.id,
  ethereum: mainnet.id,
  base: base.id,
  sepolia: sepolia.id,
} as const;

/** Per-chain progress surfaced to the UI during a multi-chain claim. */
export type ChainClaimProgress = {
  /** Chain currently being claimed on (after switch). */
  currentChainId: number;
  /** 1-based index of the current chain in the claim sequence. */
  currentChainIndex: number;
  /** Total number of chains involved in this claim. */
  totalChains: number;
  /** Chain IDs already fully claimed in this run. */
  completedChainIds: number[];
};

type UseClaimRewardsProps = {
  onClaimSuccess?: () => void;
  onClaimError?: (error: ClaimRewardsError) => void;
  queryKeys: {
    claimableRewards: string;
    claimableSoonRewards?: string;
  };
  fetchers: {
    getClaimableRewards: (userAddress: string) => Promise<ClaimableRewards>;
    simulateClaim: (claimItems: ClaimItem[], chainId?: number) => Promise<void>;
    getClaimableSoonRewards?: (userAddress: string) => Promise<ClaimableSoonEpochRewards[]>;
  };
  metricsEvent: MetricsEvents;
  enabled?: boolean;
  /** User-facing message for a failed claim simulation. Defaults to the rewards copy. */
  simulationErrorMessage?: string;
};

const DEFAULT_SIMULATION_ERROR_MESSAGE =
  "There's an issue with claiming your rewards. Our team has been notified. Please try again in 24 hours.";

export const CLAIM_REWARDS_ERROR_TYPE = {
  SIMULATION: "simulation",
  USER_REJECTED: "user-rejected",
  CONNECTOR_DISCONNECTED: "connector-disconnected",
  WALLET_MISMATCH: "wallet-mismatch",
  WALLET_LOCKED: "wallet-locked",
  UNKNOWN: "unknown",
} as const;

export type ClaimRewardsErrorType = (typeof CLAIM_REWARDS_ERROR_TYPE)[keyof typeof CLAIM_REWARDS_ERROR_TYPE];

export class ClaimRewardsError extends Error {
  type: ClaimRewardsErrorType;
  constructor(type: ClaimRewardsErrorType, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "ClaimRewardsError";
    this.type = type;
  }
}

export type RewardEpochDetails = {
  epochId: number;
  activeEpochId: number;
  epochStart: Date;
  epochEnd: Date;
  rewardTokenSymbol: string;
  amount: number;
  /**
   * USD value of `amount` derived from the live token prices, or `null` when the
   * reward token has no known price (e.g. a freshly launched issuer token).
   * Display layers must treat `null` as "unpriced" — never assume a 1:1 USD peg.
   */
  amountFiat: number | null;
  cooldownEnd?: string;
  /**
   * Chain the reward is distributed on. Optional because claimable-soon rewards
   * don't carry it and the API hasn't fully rolled out per-claim chainId yet.
   */
  chainId?: number;
  /** On-chain reward token address (for multichain token disambiguation). */
  rewardTokenAddress?: string;
  campaignData?: {
    id: string;
    name: string;
    icon: string | null;
    title: string;
  };
};

/**
 * The cooldown period (in milliseconds) after a successful claim.
 * During this period, the claim button should be disabled to prevent duplicate claims
 * while the backend/indexer updates the claimable rewards state.
 */
const CLAIM_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Returns a unique localStorage key for storing the last claim timestamp for a given address.
 * @param address The user's defi address
 */
function getClaimCooldownKey(address?: string) {
  return address ? `cede-claim-cooldown-${address}` : undefined;
}

function getClaimedEpochsStorageKey(address?: string) {
  return address ? `cede-claimed-epochs-${address}` : undefined;
}

function readClaimedEpochIds(address?: string): Set<string> {
  const key = getClaimedEpochsStorageKey(address);
  if (!key) return new Set();
  try {
    const stored = localStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function persistClaimedEpochIds(address: string, ids: Set<string>) {
  const key = getClaimedEpochsStorageKey(address);
  if (!key) return;
  if (ids.size === 0) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, JSON.stringify([...ids]));
  }
}

export const useClaimRewards = ({
  onClaimSuccess,
  onClaimError,
  queryKeys,
  fetchers,
  metricsEvent,
  enabled = false,
  simulationErrorMessage = DEFAULT_SIMULATION_ERROR_MESSAGE,
}: UseClaimRewardsProps) => {
  const queryClient = useQueryClient();
  const defaultRewardChainId = getRewardChain(clientConfig.nodeEnv).id;

  // Public clients for every chain rewards can be distributed on. Resolved once
  // per supported chain (fixed count → Rules of Hooks safe) so the per-chain
  // claim loop can wait for receipts on whichever chain a batch settles on.
  const arbitrumClient = usePublicClient({ chainId: REWARD_CHAIN_IDS.arbitrum });
  const ethereumClient = usePublicClient({ chainId: REWARD_CHAIN_IDS.ethereum });
  const baseClient = usePublicClient({ chainId: REWARD_CHAIN_IDS.base });
  const sepoliaClient = usePublicClient({ chainId: REWARD_CHAIN_IDS.sepolia });
  const publicClientsByChainId = useMemo(
    () => ({
      [REWARD_CHAIN_IDS.arbitrum]: arbitrumClient,
      [REWARD_CHAIN_IDS.ethereum]: ethereumClient,
      [REWARD_CHAIN_IDS.base]: baseClient,
      [REWARD_CHAIN_IDS.sepolia]: sepoliaClient,
    }),
    [arbitrumClient, ethereumClient, baseClient, sepoliaClient],
  );

  const { sendTransactionAsync } = useSendTransactionWithConnection();
  const { prepareMainEvmAction } = useWalletActionGuard();
  const { hubUser } = useHubUser();
  const { trackEvent } = useMetricsEvents();
  const { switchChainAsync } = useSwitchChain();
  const { data: prices, isLoading: pricesLoading } = usePrices();
  const [cooldownTimestamp, setCooldownTimestamp] = useState<number | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  // Per-chain claim progress so the UI can prompt e.g. "claimed on Arbitrum ✓ —
  // switch to Ethereum to claim the rest". Null while no multi-chain claim runs.
  const [chainClaimProgress, setChainClaimProgress] = useState<ChainClaimProgress | null>(null);

  /**
   * Use the unified debug override address hook
   * This allows admins to test with different user addresses
   */
  const userAddress = useDebugOverrideAddress();

  const [claimedEpochIds, setClaimedEpochIds] = useState<Set<string>>(new Set());

  // Tracks transactional context during claim mutation for Sentry error reporting.
  // Using a ref to avoid re-renders and allow synchronous writes from within the async mutation.
  // Tracks per-chain context: which chain we're on, which chains already settled,
  // and the failing step within the current chain group.
  const claimContextRef = useRef<{
    chainIds: number[];
    currentChainId: number | null;
    completedChainIds: number[];
    claimItems: ClaimItem[];
    completedTxHashes: string[];
    failedBatchIndex: number | null;
    failedStep: "simulation" | "chain-switch" | "send-tx" | "wait-receipt" | null;
    lastTxInput: { to: string; data: string; hash?: string } | null;
  }>({
    chainIds: [],
    currentChainId: null,
    completedChainIds: [],
    claimItems: [],
    completedTxHashes: [],
    failedBatchIndex: null,
    failedStep: null,
    lastTxInput: null,
  });

  // Read cooldown timestamp and claimed epochs from localStorage on mount or when address changes
  useEffect(() => {
    if (!userAddress) {
      setCooldownTimestamp(null);
      setClaimedEpochIds(new Set());
      return;
    }
    const key = getClaimCooldownKey(userAddress);
    const ts = key ? localStorage.getItem(key) : null;
    setCooldownTimestamp(ts ? Number(ts) : null);
    setClaimedEpochIds(readClaimedEpochIds(userAddress));
  }, [userAddress]);

  /**
   * Marks epoch IDs as claimed in both state and localStorage.
   */
  const markEpochsAsClaimed = useCallback(
    (epochKeys: string[]) => {
      if (!userAddress) return;
      setClaimedEpochIds((prev) => {
        const next = new Set(prev);
        epochKeys.forEach((id) => next.add(id));
        persistClaimedEpochIds(userAddress, next);
        return next;
      });
    },
    [userAddress],
  );

  /**
   * Returns true if the claim cooldown is active (i.e., user must wait before claiming again).
   * This is based on the last claim timestamp stored in localStorage.
   */
  const isClaimCooldownActive = !!cooldownTimestamp && Date.now() - cooldownTimestamp < CLAIM_COOLDOWN_MS;

  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.claimableRewards, hubUser?.id, userAddress],
    queryFn: async () => {
      if (!userAddress) return;
      return await fetchers.getClaimableRewards(userAddress);
    },
    enabled: !!userAddress && enabled,
  });

  const { data: claimableSoonEpochs, isLoading: isLoadingClaimableSoonEpochs } = useQuery({
    queryKey: [queryKeys.claimableSoonRewards, hubUser?.id, userAddress],
    queryFn: async () => {
      if (!userAddress || !fetchers.getClaimableSoonRewards) return;
      const result = await fetchers.getClaimableSoonRewards(userAddress);
      return result;
    },
    enabled: !!userAddress && !!fetchers.getClaimableSoonRewards && enabled,
  });

  // Filter out epochs that are already claimed (tracked in localStorage) to avoid ALREADY_CLAIMED reverts.
  // When backend catches up (indexer processes claims), those epochs disappear from `data` and we clean up localStorage.
  const filteredData = useMemo(() => {
    if (!data) return data;
    if (claimedEpochIds.size === 0) return data;

    const { filteredClaimables, filteredBatches } = filterClaimedBatches(
      data.claimablesData,
      data.batches,
      claimedEpochIds,
    );

    return { ...data, claimablesData: filteredClaimables, batches: filteredBatches };
  }, [data, claimedEpochIds]);

  // Cleanup: remove claimed epoch IDs from localStorage when the backend no longer returns them
  // (meaning the indexer has caught up and the on-chain state is reflected in the API).
  useEffect(() => {
    if (!data || !userAddress || claimedEpochIds.size === 0) return;
    const backendEpochIds = new Set(
      data.claimablesData.map((c) => toClaimEpochKey(c.calldata.epochId, c.calldata.cedeSafe)),
    );
    const stillRelevant = [...claimedEpochIds].filter((id) => backendEpochIds.has(id));
    if (stillRelevant.length < claimedEpochIds.size) {
      const cleaned = new Set(stillRelevant);
      setClaimedEpochIds(cleaned);
      persistClaimedEpochIds(userAddress, cleaned);
    }
  }, [data, userAddress, claimedEpochIds]);

  // USD value from the live price feed; null when the token has no known price
  // (e.g. a freshly launched issuer token) so the UI can show "unpriced" honestly
  // instead of assuming a USDT peg.
  const toAmountFiat = useCallback(
    (amount: number, rewardTokenSymbol: string): number | null => {
      const tokenPrice = prices?.[rewardTokenSymbol]?.average;
      if (tokenPrice == null) return null;
      return Number(SafeMath.multiply(amount, tokenPrice));
    },
    [prices],
  );

  const rewardDetails = useMemo(() => {
    const result = new Map<string, RewardEpochDetails[]>();
    if (!filteredData || Object.keys(filteredData).length === 0) return result;
    filteredData.claimablesData.forEach((claimable) => {
      if (!result.has(claimable.campaign)) {
        result.set(claimable.campaign, []);
      }
      const amount = SafeMath.divide(claimable.calldata.amount, 10 ** claimable.tokenDecimals);
      result.get(claimable.campaign)?.push({
        campaignData: claimable.campaignData,
        epochId: claimable.calldata.epochId,
        activeEpochId: claimable.activeEpochId,
        rewardTokenSymbol: claimable.rewardTokenSymbol,
        epochStart: claimable.epochStart,
        epochEnd: claimable.epochEnd,
        amount,
        amountFiat: toAmountFiat(amount, claimable.rewardTokenSymbol),
        chainId: claimable.chainId,
        rewardTokenAddress: claimable.rewardTokenAddress,
      });
    });
    return result;
  }, [filteredData, toAmountFiat]);

  const totalRewardAmountFiat = useMemo(() => {
    if (!filteredData || Object.keys(filteredData).length === 0 || !prices) return 0;
    const totalRewardAmount = filteredData.claimablesData.reduce((acc, claimable) => {
      const amountWithDecimal = SafeMath.divide(claimable.calldata.amount, 10 ** claimable.tokenDecimals);
      const tokenPrice = prices[claimable.rewardTokenSymbol]?.average;
      if (!tokenPrice) return acc;
      const fiatAmount = SafeMath.multiply(amountWithDecimal, tokenPrice);
      return acc + Number(fiatAmount);
    }, 0);
    return totalRewardAmount;
  }, [filteredData, prices]);

  const claimableSoonRewardDetails = useMemo(() => {
    const result = new Map<string, RewardEpochDetails[]>();
    if (!claimableSoonEpochs) return result;
    claimableSoonEpochs.forEach((claimable) => {
      if (!result.has(claimable.campaign)) {
        result.set(claimable.campaign, []);
      }
      result.get(claimable.campaign)?.push({
        epochId: claimable.epochId,
        rewardTokenSymbol: claimable.rewardTokenSymbol,
        amount: claimable.amount,
        amountFiat: toAmountFiat(claimable.amount, claimable.rewardTokenSymbol),
        cooldownEnd: claimable.cooldownEnd,
        activeEpochId: claimable.activeEpochId,
        epochStart: claimable.epochStart,
        epochEnd: claimable.epochEnd,
        campaignData: claimable.campaignData,
      });
    });
    return result;
  }, [claimableSoonEpochs, toAmountFiat]);

  const totalSoonRewardAmountFiat = useMemo(() => {
    if (!claimableSoonEpochs || !prices) return 0;
    const totalRewardAmount = claimableSoonEpochs.reduce((acc, claimable) => {
      const tokenPrice = prices[claimable.rewardTokenSymbol]?.average;
      if (!tokenPrice) return acc;
      const fiatAmount = SafeMath.multiply(claimable.amount, tokenPrice);
      return acc + Number(fiatAmount);
    }, 0);
    return totalRewardAmount;
  }, [claimableSoonEpochs, prices]);

  const { mutate: claimReward, isPending: isClaiming } = useMutation({
    mutationFn: async () => {
      try {
        if (!filteredData || !userAddress) return;

        // Reset claim context for this attempt
        claimContextRef.current = {
          chainIds: [],
          currentChainId: null,
          completedChainIds: [],
          claimItems: [],
          completedTxHashes: [],
          failedBatchIndex: null,
          failedStep: null,
          lastTxInput: null,
        };

        // Group claims by chain. A user with rewards on multiple chains claims
        // one chain at a time: switch network → simulate → send that chain's
        // batches → wait for receipts, then repeat for the next chain. Each batch
        // carries the exact claims it settles (decoded from its own calldata), so
        // nothing is correlated by array position.
        const chainGroups = groupBatchesByChain(filteredData.batches, defaultRewardChainId);
        claimContextRef.current.chainIds = chainGroups.map((g) => g.chainId);

        const totalChains = chainGroups.length;
        // Local-harness override (development/local-rewards-e2e): claim against the
        // rewards module deployed on the anvil forks instead of the env default.
        const to = getAddress(
          clientConfig.localRewardsSafeModuleAddress ?? getRewardsSafeModuleAddress(clientConfig.nodeEnv),
        );

        // Aggregate batch progress is across ALL chains so the existing
        // "transaction N of M" UI keeps counting every tx in the claim. Groups
        // only contain sendable batches (empty sentinels are dropped in grouping).
        const totalBatches = chainGroups.reduce((acc, g) => acc + g.batches.length, 0);
        if (totalBatches > 1) {
          setBatchProgress({ current: 0, total: totalBatches });
        }

        let chainIndex = 0;
        let batchIndex = 0;
        for (const group of chainGroups) {
          chainIndex++;
          claimContextRef.current.currentChainId = group.chainId;
          setChainClaimProgress({
            currentChainId: group.chainId,
            currentChainIndex: chainIndex,
            totalChains,
            completedChainIds: [...claimContextRef.current.completedChainIds],
          });

          // Switch to this chain if the wallet isn't already on it.
          const { chainId: connectedChainId } = await prepareMainEvmAction();
          if (connectedChainId !== group.chainId) {
            claimContextRef.current.failedStep = "chain-switch";
            await switchChainAsync({ chainId: group.chainId });
          }

          const publicClient = publicClientsByChainId[group.chainId];
          if (!publicClient) {
            // No silent fallback: we cannot wait for receipts on an
            // unconfigured chain, so surface it rather than guessing a client.
            throw new ClaimRewardsError(
              CLAIM_REWARDS_ERROR_TYPE.UNKNOWN,
              `No public client configured for reward chain ${group.chainId}`,
            );
          }

          // Simulate this chain's claims - throws on failure (for Sentry reporting).
          // Items come from decoding the batches' own calldata, so we simulate
          // exactly what we send.
          const claimItems: ClaimItem[] = group.batches.flatMap((b) => b.claimItems);
          claimContextRef.current.claimItems = claimItems;
          try {
            claimContextRef.current.failedStep = "simulation";
            await fetchers.simulateClaim(claimItems, group.chainId);
          } catch (simulationError) {
            throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.SIMULATION, simulationErrorMessage, {
              cause: simulationError,
            });
          }

          // Execute this chain's batched transactions sequentially.
          for (const { batch, claimItems: batchClaimItems } of group.batches) {
            batchIndex++;
            if (totalBatches > 1) {
              setBatchProgress({ current: batchIndex, total: totalBatches });
            }

            claimContextRef.current.failedStep = "send-tx";
            claimContextRef.current.failedBatchIndex = batchIndex;
            claimContextRef.current.lastTxInput = { to, data: batch.batchCalldata };
            // Estimate gas ourselves (with a 20% buffer) instead of relying on the
            // wallet: when a wallet's own simulation backend can't reproduce the
            // chain state it falls back to a too-low default and the claim reverts
            // out-of-gas.
            const gasEstimate = await publicClient.estimateGas({
              account: getAddress(userAddress as string),
              to,
              data: batch.batchCalldata as `0x${string}`,
            });
            const hash = await sendTransactionAsync({
              chainId: group.chainId,
              to,
              data: batch.batchCalldata as `0x${string}`,
              gas: (gasEstimate * 120n) / 100n,
            });

            // Wait for transaction confirmation before next batch
            claimContextRef.current.failedStep = "wait-receipt";
            claimContextRef.current.lastTxInput = { ...claimContextRef.current.lastTxInput!, hash };
            const receipt = await publicClient.waitForTransactionReceipt({ hash });
            // A mined-but-reverted tx still yields a receipt — without this check the
            // epochs below would be marked claimed in localStorage and disappear from
            // the claimable list even though nothing was paid out.
            if (receipt.status !== "success") {
              throw new ClaimRewardsError(
                CLAIM_REWARDS_ERROR_TYPE.UNKNOWN,
                "The claim transaction reverted on-chain. Your rewards were not claimed — please try again.",
              );
            }

            claimContextRef.current.completedTxHashes.push(hash);
            claimContextRef.current.failedStep = null;
            claimContextRef.current.failedBatchIndex = null;

            // Mark this batch's confirmed epochs as claimed in localStorage so a retry
            // after a later batch failure won't re-send them.
            markEpochsAsClaimed(batchClaimItems.map((item) => toClaimEpochKey(item.epochId, item.cedeSafe)));
          }

          claimContextRef.current.completedChainIds.push(group.chainId);
        }

        setBatchProgress(null);
        setChainClaimProgress(null);

        // Track successful rewards claim with total fiat value
        trackEvent(metricsEvent, {
          totalRewardAmountFiat: totalRewardAmountFiat,
        });

        // Invalidate claimable rewards query to avoid user trying to claim the claimed rewards
        queryClient.invalidateQueries({ queryKey: [queryKeys.claimableRewards, hubUser?.id] });
        // A claim moves totalRewardsReceived; user stats no longer polls, so refresh it here.
        invalidateUserStats(queryClient);
      } catch (error) {
        if (error instanceof ClaimRewardsError) {
          throw error;
        }
        if (error instanceof UserRejectedRequestError) {
          throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.USER_REJECTED, "Transaction cancelled");
        }
        if (error instanceof WalletConnectionError) {
          if (error.code === WALLET_ACTION_ERROR_CODE.WALLET_MISMATCH) {
            throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.WALLET_MISMATCH, error.message, { cause: error });
          }
          if (error.code === WALLET_ACTION_ERROR_CODE.WALLET_LOCKED) {
            throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.WALLET_LOCKED, error.message, { cause: error });
          }
          throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.CONNECTOR_DISCONNECTED, error.message, { cause: error });
        }
        if (isWalletActionError(error)) {
          if (error.code === WALLET_ACTION_ERROR_CODE.USER_REJECTED) {
            throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.USER_REJECTED, error.message, { cause: error });
          }
          if (error.code === WALLET_ACTION_ERROR_CODE.WALLET_MISMATCH) {
            throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.WALLET_MISMATCH, error.message, { cause: error });
          }
          if (error.code === WALLET_ACTION_ERROR_CODE.WALLET_LOCKED) {
            throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.WALLET_LOCKED, error.message, { cause: error });
          }
          throw new ClaimRewardsError(CLAIM_REWARDS_ERROR_TYPE.CONNECTOR_DISCONNECTED, error.message, { cause: error });
        }
        if (isConnectorConnectionError(error)) {
          throw new ClaimRewardsError(
            CLAIM_REWARDS_ERROR_TYPE.CONNECTOR_DISCONNECTED,
            error instanceof Error
              ? error.message
              : "Your wallet appears to be disconnected. Please reconnect your wallet and try again.",
            { cause: error },
          );
        }
        const originalMessage = error instanceof Error ? error.message : String(error);
        throw new ClaimRewardsError(
          CLAIM_REWARDS_ERROR_TYPE.UNKNOWN,
          `An unknown error occurred while claiming rewards: ${originalMessage}`,
          {
            cause: error,
          },
        );
      }
    },
    onSuccess: async () => {
      // Set cooldown timestamp in localStorage
      if (userAddress) {
        const key = getClaimCooldownKey(userAddress);
        if (key) {
          localStorage.setItem(key, Date.now().toString());
          setCooldownTimestamp(Date.now());
        }
        // Set a timeout to invalidate the claimable rewards query after cooldown
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: [queryKeys.claimableRewards, hubUser?.id] });
        }, CLAIM_COOLDOWN_MS);
      }
      onClaimSuccess?.();
    },
    onError: (error) => {
      setBatchProgress(null);
      setChainClaimProgress(null);
      if (
        (error as ClaimRewardsError).type !== CLAIM_REWARDS_ERROR_TYPE.USER_REJECTED &&
        (error as ClaimRewardsError).type !== CLAIM_REWARDS_ERROR_TYPE.CONNECTOR_DISCONNECTED &&
        (error as ClaimRewardsError).type !== CLAIM_REWARDS_ERROR_TYPE.WALLET_MISMATCH &&
        (error as ClaimRewardsError).type !== CLAIM_REWARDS_ERROR_TYPE.WALLET_LOCKED
      ) {
        Sentry.captureException(error, {
          level: "error",
          tags: {
            feature: "rewards-claim",
            errorType: (error as ClaimRewardsError).type,
          },
          extra: {
            userAddress,
            hubUserId: hubUser?.id,
            // Chain on which the claim was in-flight when it failed.
            chainId: claimContextRef.current.currentChainId,
            ...claimContextRef.current,
          },
        });
      }
      onClaimError?.(error as ClaimRewardsError);
    },
  });

  // Distinct chains the user currently has claimable rewards on, in first-seen
  // order. Drives the per-chain sections + network labels in the claim UI.
  // TEMPORARY ROLLOUT SHIM: `chainId` is optional until get-claimable-rewards
  // populates it; absent items fall back to the default reward chain so
  // single-chain users keep working. Remove `?? defaultRewardChainId` once the
  // API always sends chainId.
  const rewardChainIds = useMemo(() => {
    if (!filteredData) return [];
    const seen = new Set<number>();
    const ids: number[] = [];
    for (const claimable of filteredData.claimablesData) {
      const chainId = claimable.chainId ?? defaultRewardChainId;
      if (!seen.has(chainId)) {
        seen.add(chainId);
        ids.push(chainId);
      }
    }
    return ids;
  }, [filteredData, defaultRewardChainId]);

  return {
    totalRewardAmountFiat,
    address: userAddress,
    rewardDetails,
    claimableSoonRewardDetails,
    claimableSoonEpochs,
    totalSoonRewardAmountFiat,
    isLoadingClaimableSoonEpochs,
    isLoading: isLoading || isClaiming || pricesLoading,
    claimReward,
    isClaimCooldownActive,
    // Raw claimables data with calldata and merkle proofs for individual claiming
    claimablesData: filteredData?.claimablesData,
    // Batch progress for UI display when claiming multiple batches
    batchProgress,
    // Per-chain progress for the multi-chain "switch network to claim the rest" UI
    chainClaimProgress,
    // Chains the user has claimable rewards on (drives per-chain UI sections)
    rewardChainIds,
    // Total number of batches for warning display
    totalBatches: filteredData?.batches?.filter((b) => b.batchCalldata !== "0x").length ?? 1,
  };
};
